import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, Sparkles } from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';

/**
 * UpgradePrompt - Shows when a user tries to access a feature above their tier
 * 
 * Usage:
 * <UpgradePrompt 
 *   feature="route-optimization" 
 *   compact={false}
 *   onClose={() => {}}
 * />
 */
const UpgradePrompt = ({ 
  feature, 
  compact = false, 
  onClose = null,
  className = '' 
}) => {
  const { getFeatureInfo, getFeatureTier, subscriptionPlan } = useBusinessContext();
  
  const featureInfo = getFeatureInfo(feature);
  const requiredTier = getFeatureTier(feature);
  
  const tierColors = {
    'Pro': {
      bg: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      border: 'border-blue-200',
      badge: 'bg-blue-600',
      button: 'bg-blue-600 hover:bg-blue-700',
      text: 'text-blue-900',
      subtext: 'text-blue-700',
    },
    'Enterprise': {
      bg: 'bg-gradient-to-br from-purple-50 to-indigo-50',
      border: 'border-purple-200',
      badge: 'bg-purple-600',
      button: 'bg-purple-600 hover:bg-purple-700',
      text: 'text-purple-900',
      subtext: 'text-purple-700',
    },
  };
  
  const colors = tierColors[requiredTier] || tierColors['Pro'];
  
  // Compact version - inline badge/link
  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <span className={`${colors.badge} text-white text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1`}>
          <Lock size={10} />
          {requiredTier}
        </span>
        <Link 
          to="/settings?tab=subscription" 
          className={`text-xs ${colors.subtext} hover:underline font-medium`}
        >
          Upgrade to unlock
        </Link>
      </div>
    );
  }
  
  // Full version - card with details
  return (
    <div className={`${colors.bg} ${colors.border} border-2 rounded-xl p-6 ${className}`}>
      <div className="flex items-start gap-4">
        <div className={`${colors.badge} text-white p-3 rounded-xl flex-shrink-0`}>
          {featureInfo?.icon ? (
            <span className="text-2xl">{featureInfo.icon}</span>
          ) : (
            <Lock size={24} />
          )}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-bold text-lg ${colors.text}`}>
              {featureInfo?.name || 'Premium Feature'}
            </h3>
            <span className={`${colors.badge} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
              {requiredTier}
            </span>
          </div>
          
          <p className="text-gray-600 text-sm mb-4">
            {featureInfo?.description || `This feature requires the ${requiredTier} plan.`}
          </p>
          
          <div className="flex items-center gap-3">
            <Link
              to="/settings?tab=subscription"
              className={`${colors.button} text-white px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors`}
            >
              <Sparkles size={16} />
              Upgrade to {requiredTier}
              <ArrowRight size={16} />
            </Link>
            
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Maybe later
              </button>
            )}
          </div>
          
          {subscriptionPlan === 'Solo' && requiredTier === 'Pro' && (
            <p className="text-xs text-gray-500 mt-3">
              Pro plan is just $49/month — includes route optimization, automated emails, and up to 15 team members
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * FeatureGate - Wraps content and shows upgrade prompt if user doesn't have access
 * 
 * Usage:
 * <FeatureGate feature="route-optimization" fallback={<UpgradePrompt />}>
 *   <RouteOptimizationControls />
 * </FeatureGate>
 */
export const FeatureGate = ({ 
  feature, 
  children, 
  fallback = null,
  showUpgradePrompt = true,
  compact = false,
}) => {
  const { canAccessFeature } = useBusinessContext();
  
  if (canAccessFeature(feature)) {
    return children;
  }
  
  if (fallback) {
    return fallback;
  }
  
  if (showUpgradePrompt) {
    return <UpgradePrompt feature={feature} compact={compact} />;
  }
  
  return null;
};

/**
 * LockedOverlay - Shows a lock icon overlay on disabled features
 * 
 * Usage:
 * <LockedOverlay feature="route-optimization">
 *   <button disabled>Optimize Route</button>
 * </LockedOverlay>
 */
export const LockedOverlay = ({ feature, children, className = '' }) => {
  const { canAccessFeature, getFeatureTier } = useBusinessContext();
  
  if (canAccessFeature(feature)) {
    return children;
  }
  
  const requiredTier = getFeatureTier(feature);
  
  return (
    <div className={`relative ${className}`}>
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900/10 rounded-lg">
        <Link
          to="/settings?tab=subscription"
          className="bg-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Lock size={14} />
          {requiredTier}
        </Link>
      </div>
    </div>
  );
};

export default UpgradePrompt;